import { Box, Chip, ChipProps, Stack, TablePagination } from "@mui/material";
import { GridColDef, GridPaginationModel } from "@mui/x-data-grid-premium";
import { HotkeyCompliantDataGrid } from "components/common/dataGrid/HotkeyCompliantDataGrid";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useTenantTranslation } from "hooks/formatters";
import { memo, useCallback, useMemo, useState } from "react";
import {
  LoadingEditRead,
  LoadingReviewDecision,
} from "src/store/api/generatedApi";

dayjs.extend(utc);

const PAGE_SIZE_OPTIONS = [25, 50, 100];
const AUTHORED_FORMAT = "YYYY-MM-DD HH:mm";
const SPAN_START_FORMAT = "MMM D HH:mm:ss";
const SPAN_END_FORMAT = "HH:mm:ss";

type EditStatus = "applied" | "pending" | "declined" | "revoked";

const editStatusOf = (decision: LoadingReviewDecision | null): EditStatus =>
  decision === null
    ? "pending"
    : decision === "approved"
      ? "applied"
      : decision;

const STATUS_CHIP: Record<EditStatus, Pick<ChipProps, "variant" | "color">> = {
  applied: { color: "success" },
  pending: { variant: "outlined" },
  declined: { variant: "outlined" },
  revoked: { color: "error", variant: "outlined" },
};

const authorName = (user: LoadingEditRead["created_by"]): string =>
  user?.name ?? user?.email ?? "—";

export const LoadingEditLedgerTable = memo(
  ({
    edits,
    highlightedEditIds,
    onEditClick,
  }: {
    edits: readonly LoadingEditRead[];
    highlightedEditIds?: ReadonlySet<number>;
    onEditClick?: (edit: LoadingEditRead) => void;
  }) => {
    const { t } = useTenantTranslation();

    const columns = useMemo<GridColDef<LoadingEditRead>[]>(
      () => [
        {
          field: "area_name",
          headerName: t("area"),
          flex: 1,
          minWidth: 110,
        },
        {
          field: "span",
          headerName: t("time"),
          flex: 2,
          minWidth: 230,
          valueGetter: (_value, row) =>
            `${dayjs.utc(row.start_time).format(SPAN_START_FORMAT)}–${dayjs
              .utc(row.end_time)
              .format(SPAN_END_FORMAT)}`,
        },
        {
          field: "assignment",
          headerName: t("assignment"),
          flex: 1,
          minWidth: 120,
          valueGetter: (_value, row) =>
            row.target_status === "loading"
              ? `${row.target_heat_id ?? "?"}·${row.target_basket_number ?? "?"}`
              : t("idle"),
        },
        {
          field: "source",
          headerName: t("source"),
          flex: 0.8,
          minWidth: 100,
          renderCell: (params) => (
            <Chip
              size="small"
              variant="outlined"
              label={t(params.row.source)}
            />
          ),
        },
        {
          field: "status",
          headerName: t("status"),
          flex: 0.9,
          minWidth: 110,
          valueGetter: (_value, row) => t(editStatusOf(row.latest_decision)),
          renderCell: (params) => {
            const status = editStatusOf(params.row.latest_decision);
            return (
              <Chip size="small" {...STATUS_CHIP[status]} label={t(status)} />
            );
          },
        },
        {
          field: "rationale",
          headerName: t("rationale"),
          flex: 1.5,
          minWidth: 160,
          valueGetter: (_value, row) => row.rationale ?? "",
        },
        {
          field: "reviewer",
          headerName: t("reviewer"),
          flex: 1.4,
          minWidth: 160,
          valueGetter: (_value, row) => {
            if (row.reviewed_at === null) return "";
            const at = `${dayjs.utc(row.reviewed_at).format(AUTHORED_FORMAT)} UTC`;
            return row.reviewed_by
              ? `${authorName(row.reviewed_by)} · ${at}`
              : at;
          },
        },
        {
          field: "created_at",
          headerName: t("authored"),
          flex: 1.4,
          minWidth: 150,
          valueGetter: (_value, row) =>
            `${dayjs.utc(row.created_at).format(AUTHORED_FORMAT)} UTC`,
        },
        {
          field: "author",
          headerName: t("author"),
          flex: 1.2,
          minWidth: 140,
          valueGetter: (_value, row) => authorName(row.created_by),
        },
      ],
      [t]
    );

    const getRowId = useCallback((row: LoadingEditRead) => row.id, []);

    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>(
      {
        page: 0,
        pageSize: 50,
      }
    );

    return (
      <Stack spacing={1}>
        <TablePagination
          component="div"
          count={edits.length}
          page={paginationModel.page}
          onPageChange={(_event, page) => {
            setPaginationModel((model) => ({ ...model, page }));
          }}
          rowsPerPage={paginationModel.pageSize}
          onRowsPerPageChange={(event) => {
            setPaginationModel({
              page: 0,
              pageSize: Number(event.target.value),
            });
          }}
          rowsPerPageOptions={PAGE_SIZE_OPTIONS}
          sx={{ alignSelf: "flex-end" }}
        />
        <Box
          sx={{
            "& .ledger-row-highlighted": { bgcolor: "action.selected" },
            ...(onEditClick && {
              "& .MuiDataGrid-row": { cursor: "pointer" },
            }),
          }}
        >
          <HotkeyCompliantDataGrid<LoadingEditRead>
            autoHeight
            hideFooter
            rows={edits}
            columns={columns}
            getRowId={getRowId}
            getRowClassName={(params) =>
              highlightedEditIds?.has(params.row.id)
                ? "ledger-row-highlighted"
                : ""
            }
            onRowClick={(params) => {
              onEditClick?.(params.row as LoadingEditRead);
            }}
            disableColumnSelector
            disableRowSelectionOnClick
            pagination
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            initialState={{
              sorting: { sortModel: [{ field: "created_at", sort: "desc" }] },
            }}
          />
        </Box>
      </Stack>
    );
  }
);
